import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon as CheckCircleSolid,
  XCircleIcon as XCircleSolid,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { toggleMinimize, removeUploadJob, clearCompletedJobs } from '../../store/slices/uploadSlice';
import { cancelUpload } from '../../utils/uploadTracker';
import { playSuccessChime } from '../../utils/sound';

const UploadWidget: React.FC = () => {
  const dispatch = useAppDispatch();
  const { jobs, isMinimized } = useAppSelector((state) => state.upload);
  const prevUploadingCountRef = React.useRef(0);

  React.useEffect(() => {
    const activeCount = jobs.filter(
      (j) => j.status === 'uploading' || j.status === 'processing'
    ).length;

    if (prevUploadingCountRef.current > 0 && activeCount === 0) {
      const hasCompleted = jobs.some((j) => j.status === 'completed');
      if (hasCompleted) {
        playSuccessChime();
      }
    }

    prevUploadingCountRef.current = activeCount;
  }, [jobs]);

  if (jobs.length === 0) return null;

  const completedCount = jobs.filter((job) => job.status === 'completed').length;
  const totalCount = jobs.length;

  return (
    <div className="fixed bottom-4 right-4 z-[20000] w-80 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-bold text-gray-900 dark:text-white">
            {completedCount === totalCount
              ? 'Uploads completed'
              : `Uploading (${completedCount}/${totalCount})`}
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={() => dispatch(toggleMinimize())}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md text-gray-500"
          >
            {isMinimized ? (
              <ChevronUpIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
          </button>
          {completedCount === totalCount && (
            <button
              onClick={() => dispatch(clearCompletedJobs())}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md text-gray-500"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* JobList */}
      <AnimatePresence>
        {!isMinimized && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="max-h-60 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800"
          >
            {jobs.map((job) => (
              <div key={job.id} className="p-3 flex items-center space-x-3">
                {/* Thumbnail / Indicator */}
                <div className="relative w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-md overflow-hidden flex-shrink-0">
                  {job.thumbnailUrl ? (
                    <img
                      src={job.thumbnailUrl}
                      alt={job.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {job.status === 'completed' ? (
                        <CheckCircleSolid className="w-5 h-5 text-green-500" />
                      ) : job.status === 'error' ? (
                        <XCircleSolid className="w-5 h-5 text-red-500" />
                      ) : (
                        <ArrowPathIcon className="w-4 h-4 text-gray-400 animate-spin" />
                      )}
                    </div>
                  )}
                </div>

                {/* Progress Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                      {job.title}
                    </span>
                    {job.status === 'completed' && (
                      <CheckCircleSolid className="w-4 h-4 text-green-500" />
                    )}
                    {job.status === 'error' && (
                      <XCircleSolid className="w-4 h-4 text-red-500" />
                    )}
                  </div>

                  {job.status === 'uploading' && (
                    <div className="w-full">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-primary-500 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${job.progress}%` }}
                        ></div>
                      </div>
                      <span className="text-[10px] text-gray-400 mt-1 block">
                        Uploading... {job.progress}%
                      </span>
                    </div>
                  )}

                  {job.status === 'processing' && (
                    <div className="flex items-center space-x-1 text-[10px] text-gray-400">
                      <ArrowPathIcon className="w-3 h-3 animate-spin" />
                      <span>Processing...</span>
                    </div>
                  )}

                  {job.status === 'completed' && (
                    <span className="text-[10px] text-green-500">Completed</span>
                  )}
                  {job.status === 'error' && (
                    <span className="text-[10px] text-red-500 truncate">
                      {job.error || 'Failed'}
                    </span>
                  )}
                </div>

                {/* Cancel Button */}
                <button
                  onClick={() => {
                    if (job.status === 'uploading' || job.status === 'processing') {
                      cancelUpload(job.id);
                    }
                    dispatch(removeUploadJob(job.id));
                  }}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-400"
                  title={job.status === 'uploading' || job.status === 'processing' ? 'Cancel upload' : 'Remove from list'}
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UploadWidget;
